import React, { createContext, useState } from 'react';
import axios, { AxiosResponse } from 'axios';
import {
  jwtCreate,
  jwtRefresh,
  JWTCreateResponse,
  JWTRefreshResponse,
  jwtValidate,
  userMeInfo,
  UserInfoResponse,
  authenticateGitHub,
} from '../api/accounts';
import {
  ACCESS_TOKEN_LOCALSTORAGE_KEY,
  REFRESH_TOKEN_LOCALSTORAGE_KEY
} from '../config/constants';

export interface AuthContextState {
  authState: boolean
  me: UserInfoResponse | null
  checkAuthenticated: () => Promise<boolean>
  fetchMe: () => Promise<void>
  authenticateUser: (username: string, password: string) => Promise<void>
  authenticateGitHubUser: (code: string) => Promise<void>
  logoutUser: () => void
  refreshRetryRequest: (func: (accessToken: string | null) => Promise<AxiosResponse>) => Promise<AxiosResponse>
};

// initiate auth context
export const AuthContext = createContext<AuthContextState | null>(null);

// auth context provider
export const AuthProvider = ({ children }: any): JSX.Element | null => {
  const [authState, setAuthState] = useState<boolean>(false);
  const [me, setMe] = useState<UserInfoResponse | null>(null);

  // get access token from local storage
  const retrieveAccessToken = (): string | null => {
    return localStorage.getItem(ACCESS_TOKEN_LOCALSTORAGE_KEY);
  };

  // get refresh token from local storage
  const retrieveRefreshToken = (): string | null => {
    return localStorage.getItem(REFRESH_TOKEN_LOCALSTORAGE_KEY);
  };

  // store access token in local storage
  const storeAccessToken = (accessToken: string): void => {
    localStorage.setItem(ACCESS_TOKEN_LOCALSTORAGE_KEY, accessToken);
  };

  // store refresh token in local storage
  const storeRefreshToken = (refreshToken: string): void => {
    localStorage.setItem(REFRESH_TOKEN_LOCALSTORAGE_KEY, refreshToken);
  };

  // remove access token from local storage
  const clearAccessToken = (): void => {
    localStorage.removeItem(ACCESS_TOKEN_LOCALSTORAGE_KEY);
  };

  // remove refresh token from local storage
  const clearRefreshToken = (): void => {
    localStorage.removeItem(REFRESH_TOKEN_LOCALSTORAGE_KEY);
  };

  // use API request response to detect if authentication has failed
  // this is used to determine whether token should be refreshed
  const hasAuthenticationFailed = (status: number, data: any): boolean => {
    return (
      status >= 400 &&
      data?.code === 'token_not_valid'
    );
  };

  // check if user is authenticated
  const checkAuthenticated = async (): Promise<boolean> => {
    const accessToken = retrieveAccessToken();
    const refreshToken = retrieveRefreshToken();
    let response: AxiosResponse;

    if (accessToken != null) {
      try {
        response = await jwtValidate(accessToken)
        if (response.status === 200) {
          setAuthState(true);
          return true;
        }
      } catch (error: any) {
        // continue with function
      }
    }

    if (refreshToken != null) {
      try {
        response = await jwtValidate(refreshToken)
        if (response.status === 200) {
          setAuthState(true);
          return true;
        }
      }
      catch (error: any) {
        // continue with function
      }
    }

    setAuthState(false);
    return false;
  };

  // authenticate user using username/email and password
  // update tokens after authentication
  const authenticateUser = async (usernameOrEmail: string, password: string): Promise<void> => {
    const response: AxiosResponse = await jwtCreate(usernameOrEmail, password);
    const responseData: JWTCreateResponse = response.data as JWTCreateResponse;

    storeAccessToken(responseData.access);
    storeRefreshToken(responseData.refresh);
  };

  // authenticate use using github authorization code
  // update tokens after authentication
  const authenticateGitHubUser = async (code: string): Promise<void> => {
    const response: AxiosResponse = await authenticateGitHub(code);
    const responseData: JWTCreateResponse = response.data as JWTCreateResponse;

    storeAccessToken(responseData.access);
    storeRefreshToken(responseData.refresh);
  };

  const fetchMe = async (): Promise<void> => {
    refreshRetryRequest(
      userMeInfo
    ).then((response: AxiosResponse) => {
      setMe(response.data as UserInfoResponse);
    });
  }

  // log out user
  // i.e. remove user tokens
  const logoutUser = (): void => {
    clearAccessToken();
    clearRefreshToken();
    checkAuthenticated();
    setMe(null);
  };

  // refresh tokens for user
  const refreshTokens = async (): Promise<void> => {
    const response: AxiosResponse = await jwtRefresh(retrieveRefreshToken() as string);
    const responseData: JWTRefreshResponse = response.data as JWTRefreshResponse;

    storeAccessToken(responseData.access);
  };

  // execute api function with access token
  // if response indicates authentication failure, refresh token and try again
  const refreshRetryRequest = async (func: (accessToken: string | null) => Promise<AxiosResponse>): Promise<AxiosResponse> => {
    let response: AxiosResponse | undefined;
    try {
      response = await func(retrieveAccessToken());
    } catch (error) {
      // axios error assertion
      if (axios.isAxiosError(error)) {
        // refresh tokens, then retry
        try {
          await refreshTokens();
        } catch (error) {
          logoutUser();
          throw error;
        }

        if (hasAuthenticationFailed(error.response?.status as number, error.response?.data)) {
          response = await func(retrieveAccessToken());
        } else {
          throw error;
        }
      }
    }

    return response as AxiosResponse;
  };

  // construct auth context
  const authContext: AuthContextState = {
    authState,
    me,
    checkAuthenticated,
    fetchMe,
    authenticateUser,
    authenticateGitHubUser,
    logoutUser,
    refreshRetryRequest
  };

  return (
    <AuthContext.Provider value={authContext}>
      {children}
    </AuthContext.Provider>
  );
};
